import React, { useState } from 'react'
import { Button, Input } from 'antd'
import styled from 'styled-components/macro'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  canCorrectFoodSafetyLog,
  formatFoodSafetyCorrectionLines,
} from 'src/utils/foodSafetyAdHoc'

const Note = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  line-height: 1.4;
`

const Shell = styled.div`
  margin-bottom: 12px;
`

const Form = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin-top: 8px;
  padding: 10px;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: #fff;
`

const TempLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Unit = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

/**
 * Quiet correct control for one saved temperature.
 * Shows the corrected stamp, and lets the chef type the right number.
 * The form stays in its own block, apart from Record corrective action.
 */
const FoodSafetyCorrectReading = ({ log, testId, disabled, onCorrect }) => {
  const lines = formatFoodSafetyCorrectionLines(log)
  const allowed = canCorrectFoodSafetyLog(log)
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  if (!lines && !allowed) return null

  const current = log?.payload?.finalTemp ?? log?.payload?.value
  const openForm = () => {
    setValue(current == null ? '' : String(current))
    setNote('')
    setOpen(true)
  }
  const save = async () => {
    const ok = await onCorrect({ value, note })
    if (ok) setOpen(false)
  }

  return (
    <Shell data-testid={testId}>
      {lines?.original ? <Note>{lines.original}</Note> : null}
      {lines?.by ? <Note>{lines.by}</Note> : null}
      {lines?.note ? <Note>{lines.note}</Note> : null}
      {allowed && !open ? (
        <Button
          type='link'
          size='small'
          disabled={disabled}
          onClick={openForm}
          data-testid={`${testId}-open`}
          style={{ paddingLeft: 0 }}
        >
          Correct
        </Button>
      ) : null}
      {open ? (
        <Form data-testid={`${testId}-form`}>
          <TempLine>
            <Input
              value={value}
              onChange={event => setValue(event.target.value)}
              aria-label='Corrected temperature'
              data-testid={`${testId}-value`}
              style={{ width: 88 }}
            />
            <Unit>°C</Unit>
          </TempLine>
          <Input
            value={note}
            onChange={event => setNote(event.target.value)}
            placeholder='Reason, optional'
            aria-label='Reason'
            data-testid={`${testId}-note`}
          />
          <Actions>
            <Button
              size='small'
              disabled={disabled}
              onClick={save}
              data-testid={`${testId}-save`}
            >
              Save correction
            </Button>
            <Button type='link' size='small' onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Actions>
        </Form>
      ) : null}
    </Shell>
  )
}

export default FoodSafetyCorrectReading
